import { BulkModal } from '@/components/modals/BulkModal';
import { resetAssetListQueries } from '@/hooks/api/assets/resetDeletedAssetQueries';
import { useActiveAssetDeletions } from '@/hooks/api/assets/useActiveAssetDeletions';
import { useTenants } from '@/services/tenants/useTenants';
import { ButtonBase, Paper } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { LiveProgress } from './LiveProgress';
import { useAssetDeletionDialog } from './useAssetDeletionDialog';
import { useFinishedDeletionReport } from './useFinishedDeletionReport';

export const LIST_REFRESH_INTERVAL_MS = 10_000;

const NO_ITEMS: { id: string }[] = [];

export const AssetDeletionProgress = () => {
  const { getTenantId } = useTenants();
  return <TenantAssetDeletionProgress key={getTenantId()} />;
};

const TenantAssetDeletionProgress = () => {
  const { data: deletions = [] } = useActiveAssetDeletions();
  const isRunning = deletions.length > 0;
  const { isOpen, startingCount, result, startedJobIds, open, close, clearStartedJobs } = useAssetDeletionDialog();
  const isStarting = startingCount !== undefined;
  const isActive = isRunning || isStarting;
  const trackedIds = useRef(new Set<string>());
  const { finished, reportFinishedJob, reportOnceAllAreDone } = useFinishedDeletionReport(!isActive);
  const held = useRef({ completed: 0, total: 0 });
  if (!isActive) held.current = { completed: 0, total: 0 };
  const completed = Math.max(
    held.current.completed,
    deletions.reduce((sum, job) => sum + job.deletedAssets + job.failedAssets + job.missingAssets, finished.total)
  );
  const total = Math.max(
    held.current.total,
    deletions.reduce((sum, job) => sum + job.totalAssets, finished.total)
  );
  held.current = { completed, total };
  const shownTotal = startingCount ?? total;
  const queryClient = useQueryClient();
  const lastListRefresh = useRef(0);

  useEffect(() => {
    const activeIds = new Set(deletions.map(deletion => deletion.id));
    const knownIds = [...trackedIds.current, ...startedJobIds];
    const finishedIds = new Set(knownIds.filter(id => !activeIds.has(id)));
    trackedIds.current = activeIds;
    if (startedJobIds.length > 0) clearStartedJobs();

    for (const id of finishedIds) void reportFinishedJob(id);
  }, [deletions, startedJobIds]);

  useEffect(reportOnceAllAreDone, [isActive]);

  useEffect(() => {
    if (!deletions.some(job => job.startedByRequester)) {
      lastListRefresh.current = 0;
      return;
    }
    if (lastListRefresh.current === 0) lastListRefresh.current = Date.now();
    if (Date.now() - lastListRefresh.current < LIST_REFRESH_INTERVAL_MS) return;
    lastListRefresh.current = Date.now();
    resetAssetListQueries(queryClient);
  }, [isRunning, completed]);

  return (
    <>
      {isRunning && !isOpen && (
        <Paper
          elevation={6}
          sx={{ position: 'fixed', bottom: 16, left: 16, width: 270, zIndex: 'snackbar', borderRadius: '12px' }}
        >
          <ButtonBase
            onClick={open}
            data-testid='asset-deletion-progress'
            aria-label='Show asset deletion progress'
            sx={{ display: 'block', width: '100%', padding: 1.5, borderRadius: '12px', textAlign: 'left' }}
          >
            <LiveProgress completed={completed} total={total} suffix=' assets' variant='caption' />
          </ButtonBase>
        </Paper>
      )}
      <BulkModal
        open={isOpen}
        onClose={close}
        selectedItems={NO_ITEMS}
        backgroundProgress={{
          completed: isStarting ? 0 : completed,
          total: shownTotal,
          isRunning: isActive,
          isStarting,
          result,
        }}
        labels={{
          title: 'Delete items',
          action: 'delete',
          actionPastTense: 'Deleted',
          actionPresent: 'Deleting',
          confirmButton: 'Delete all',
          itemSingular: 'item',
          itemPlural: 'items',
          description: isActive
            ? `${shownTotal.toLocaleString()} items are being deleted. This action cannot be undone.`
            : 'No deletes are running.',
        }}
      />
    </>
  );
};
